import {
  signIn,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchUserAttributes,
  type SignInOutput,
  type ConfirmSignInOutput,
} from 'aws-amplify/auth'

export type SignInInput = { username: string; password: string }
export type AuthStep = SignInOutput['nextStep']['signInStep']

export async function login({ username, password }: SignInInput): Promise<SignInOutput> {
  return signIn({ username: username.trim(), password })
}

export async function confirmLogin(
  challengeResponse: string,
): Promise<ConfirmSignInOutput> {
  return confirmSignIn({ challengeResponse: challengeResponse.replace(/\s+/g, '') })
}

export async function logout() {
  return signOut()
}

export async function requestPasswordReset(username: string) {
  return resetPassword({ username: username.trim() })
}

export async function confirmPasswordReset(
  username: string,
  confirmationCode: string,
  newPassword: string,
) {
  return confirmResetPassword({
    username: username.trim(),
    confirmationCode: confirmationCode.replace(/\s+/g, ''),
    newPassword,
  })
}

export async function loadCurrentUser() {
  try {
    const user = await getCurrentUser()
    const attrs = await fetchUserAttributes()
    return {
      username: user.username,
      email: attrs.email,
      phone: attrs.phone_number,
      name: attrs.name,
      givenName: attrs.given_name,
      familyName: attrs.family_name,
      codiceCliente: attrs['custom:codice_cliente'],
    }
  } catch {
    return null
  }
}

/**
 * Nome da mostrare nell'UI. Priorità:
 *   1. `name + family_name` (caso standard pool Enermed: `name` contiene
 *      il solo nome di battesimo, `family_name` il cognome)
 *   2. `given_name + family_name`
 *   3. `name` da solo (se contiene già "Mario Rossi" intero)
 *   4. `family_name` da solo
 *   5. fallback: `username` Cognito grezzo
 *
 * Dedup difensivo: se `family_name` è già contenuto in `name` (es. `name`
 * vale "Mario Rossi" e `family_name` "Rossi"), non lo concatena due volte.
 */
export function getDisplayName(user: {
  username: string
  name?: string
  givenName?: string
  familyName?: string
}): string {
  const name = user.name?.trim()
  const given = user.givenName?.trim()
  const family = user.familyName?.trim()

  if (name && family) {
    return name.toLowerCase().includes(family.toLowerCase()) ? name : `${name} ${family}`
  }
  if (given && family) return `${given} ${family}`
  if (name) return name
  if (family) return family
  return user.username
}

/**
 * Solo il nome di battesimo (per saluti tipo "Buongiorno, Andreea").
 * Estratto dal `name` se contiene spazi, altrimenti `given_name` o fallback.
 */
export function getFirstName(user: {
  username: string
  name?: string
  givenName?: string
}): string {
  if (user.givenName?.trim()) return user.givenName.trim()
  if (user.name?.trim()) return user.name.trim().split(/\s+/)[0]
  return user.username
}
